import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import clsx from 'clsx'

import config from 'config'
import PropTypes from 'prop-types'
import lang from '@/lang'

import SupplierService from '@/modules/supplier/supplierService'
import { errorCodes } from '@/modules/base/util/constants'
import { BASE_DATA_TYPES } from '@/modules/base/components/generics/constants'
import { DateInput } from '@/modules/base/components/inputs/index'
import { getPayKinds } from '@/modules/base/helpers/getSettingsData'
import DOSelect from '@/modules/base/components/inputs/do-select'
import { resolveDocuAiPayKindId } from './docuai-pay-kind'
import { deriveDueDate } from './due-date'

const moment = require('moment')
const { dateFormat: { api: apiDateFormat } } = config
const { NOT_FOUND } = errorCodes

const computeDueDateState = ({ dueDateFromData }) => {
  return dueDateFromData ? moment(dueDateFromData).set('hour', 12) : null
}

const isEInvoiceImportForExpense = (expenseLike) => {
  return Boolean(expenseLike?.receipts?.some((receipt) => receipt?.validationResult))
}

const useStyles = makeStyles((theme) => ({
  payKind: {
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    '& > *': {
      width: '50%',
    },
    '& > :first-child': {
      marginRight: 8,
    },
    '& > :last-child': {
      marginLeft: 8,
    },
  },
  highlight: {
    '& > * > div > input': {
      borderColor: theme.colors.yellow,
      background: theme.yellowBackground,
    },
  },
  // FLAG:useDocuAi — "needs attention": the document states a paid status but no
  // payment type is chosen; the reviewer must pick one manually (never auto-filled).
  attention: {
    border: `1px solid ${theme.red}`,
    borderRadius: theme.borderRadius,
    padding: 4,
    margin: -5,
  },
  // FLAG:useDocuAi — "suggestion": the document explicitly states a payment method
  // (identified value → yellow) but no payment type is chosen yet.
  suggestion: {
    border: `1px solid ${theme.colors.yellow}`,
    borderRadius: theme.borderRadius,
    padding: 4,
    margin: -5,
  },
}))

const ExpensePaymentConditions = (props) => {
  const classes = useStyles()
  const theme = useTheme()
  const {
    data,
    identifiedValues,
    onChange,
    checkFillAnimation,
  } = props
  const { dueDate: dueDateFromData, payKindId: payKindIdFromData, data: supplierData, filledData } = data
  const isEInvoiceImport = isEInvoiceImportForExpense(data)
  const [dueDate, setDueDate] = useState(computeDueDateState({ dueDateFromData }))
  const [payKindId, setPayKindId] = useState(supplierData?.payKindId)
  const [payKinds, setPayKinds] = useState([])
  // FLAG:useDocuAi — a manual pick permanently stops the auto-preselect from
  // re-asserting (the supplier-default effect may reset payKindId asynchronously
  // AFTER the preselect ran — the effect below re-fires on payKindId to win that
  // race, but never against the reviewer's own choice).
  const [payKindTouchedByUser, setPayKindTouchedByUser] = useState(false)

  useEffect(() => {
    setDueDate(computeDueDateState({ dueDateFromData }))
  }, [dueDateFromData])

  useEffect(() => {
    if (payKindIdFromData) {
      setPayKindId(payKindIdFromData)
    }
  }, [payKindIdFromData])

  const isDueDateFilled = useMemo(() => {
    return checkFillAnimation?.(['dueDate'])
  }, [filledData, identifiedValues])

  const isPayKindIdFilled = useMemo(() => {
    return checkFillAnimation?.(['payKindId'])
  }, [filledData, identifiedValues])

  const onDueDateChange = (date) => {
    const dueDate = moment(date).set('hour', 12)
    setDueDate(dueDate)
    onChange({ dueDate, payKindId })
  }

  const onPayKindChange = ({ target: { value } }) => {
    // A manual pick is the reviewer's own choice — the auto-preselect stays off from
    // here on, even if the supplier default later resolves and nulls payKindId.
    setPayKindTouchedByUser(true)
    setPayKindId(value)
    onChange({ dueDate: dueDate ? moment(dueDate).toISOString() : null, payKindId: value })
  }

  // `date` may be null: without a derivable rule the due date remains empty, while the payment type
  // is still preselected (both have always depended on the same call here).
  const autoUpdate = (date, id) => {
    setDueDate(date)
    setPayKindId(id)
    onChange({
      dueDate: date ? date.toDate() : null,
      payKindId: id,
    }, false)
  }

  // A due date is derived ONLY from a real rule — otherwise the field stays empty.
  // Rationale and receipt-date anchor: see ./due-date.
  const dueDateFor = (dueDays) => deriveDueDate(dueDays, data.date)

  useEffect(() => {
    if (isEInvoiceImport) {
      return
    }
    if (supplierData) {
      const { type, baseDataId: id, cashDiscountSetting, payKindId } = supplierData
      const dueDate = dueDateFor(cashDiscountSetting?.dueDays)

      if (!data.payKindId && !dueDateFromData && (cashDiscountSetting || payKindId)) {
        autoUpdate(dueDate, payKindId ?? null)
      }

      switch (type) {
        case BASE_DATA_TYPES.SUPPLIER:
          SupplierService.getById(id).then((result) => {
          // throw error if supplier was soft deleted
            if (result.deletedAt) {
              throw new Error(NOT_FOUND)
            }
            const dueDate = dueDateFor(result.cashDiscountSetting?.dueDays)
            if (!dueDateFromData) {
              autoUpdate(dueDate, payKindIdFromData ?? result.payKindId ?? null)
            }
          })
          break
        case BASE_DATA_TYPES.CUSTOMER:
          if (!dueDateFromData) {
            autoUpdate(null, null)
          }
          break
      }
    } else {
      if (!dueDateFromData) {
        autoUpdate(null, null)
      }
    }
  }, [supplierData])

  useEffect(() => {
    if (isEInvoiceImport) {
      return
    }
    if (!data.id) {
      return
    }
    if (dueDateFromData) {
      autoUpdate(moment(dueDateFromData).set('hour', 12), payKindIdFromData)
    }
  }, [data.id])

  useEffect(() => {
    getPayKinds().then(({ body: { data } }) => {
      setPayKinds(data)
    })
  }, [])

  const disabled = data.bookings?.length > 0

  // FLAG:useDocuAi — data.paymentStatus/paymentMethodCode are only ever set by the
  // invoice-review page; every legacy expense edit passes data without the keys, so
  // everything below stays inert there by construction.
  //
  // The frame follows the REVIEW VERDICT and nothing else: 'error' → red, 'warning' → the
  // yellow review color, 'ok'/'info' → no frame. It used to also turn yellow whenever the
  // document's method had auto-preselected a payment type — which is every invoice with a
  // recognized method, so the payment type was permanently yellow even though DocuAI had
  // stamped it 'ok'. That signal was about OUR guess at the orgaMAX payment type, not about
  // the extracted value, and it does not belong in the check colour.
  //
  // The one local red case survives: the document says it was paid, but no payment type is
  // chosen at all — that is a missing decision the reviewer has to make, independent of
  // anything DocuAI validated.
  const selectedPayKindId = payKindId ?? payKinds[0]?.id
  const selectedPayKind = payKinds.find(({ id }) => id === selectedPayKindId)
  const noPayKindChosen = !disabled && (!selectedPayKind || selectedPayKind.title === 'ohne Zahlart')
  const methodValidationState = data.fieldMeta?.paymentMethodCode?.validationState
  const needsPayKindAttention = methodValidationState === 'error'
    || (!!data.paymentStatus && noPayKindChosen)
  const suggestsPayKind = !needsPayKindAttention && methodValidationState === 'warning'

  useEffect(() => {
    if (disabled || payKindTouchedByUser || !data.paymentMethodCode || !payKinds.length) return
    // Re-asserts whenever the selection falls back to "ohne Zahlart" (the
    // supplier-default effect resolves asynchronously and may null payKindId
    // AFTER the first preselect) — payKindId is deliberately in the deps.
    const current = payKinds.find(({ id }) => id === (payKindId ?? payKinds[0]?.id))
    if (current && current.title !== 'ohne Zahlart') return
    const matchedPayKindId = resolveDocuAiPayKindId(payKinds, data.paymentMethodCode)
    if (matchedPayKindId === undefined) return
    setPayKindId(matchedPayKindId)
    onChange({ dueDate: dueDate ? moment(dueDate).toISOString() : null, payKindId: matchedPayKindId }, false)
  }, [payKinds, data.paymentMethodCode, payKindId, disabled, payKindTouchedByUser])

  return (
    <div className={classes.container}>
      <DateInput
        readOnly={disabled}
        label={lang.receipt.form.dueDate}
        name='dueDate'
        customClass={isDueDateFilled && classes.highlight}
        fillAnimationOptions={{ show: isDueDateFilled, color: theme.colors.yellow }}
        value={dueDate ? dueDate.format(apiDateFormat) : ''}
        required={false}
        onChange={onDueDateChange} />
      {payKinds.length && <div className={clsx(classes.payKind, needsPayKindAttention && classes.attention, suggestsPayKind && classes.suggestion)}>
        <DOSelect
          disabled={disabled}
          label={lang.supplier.forms.payConditionForm.fieldLabels.payKindMethod}
          value={payKindId ?? payKinds[0].id}
          options={payKinds.map(({ id, title }) => ({ value: id, label: title }))}
          showFillAnimation={isPayKindIdFilled}
          fillAnimationOptions={{ show: isPayKindIdFilled, color: theme.colors.yellow }}
          onChange={onPayKindChange}
        />
      </div>}
    </div>
  )
}

ExpensePaymentConditions.propTypes = {
  data: PropTypes.object.isRequired,
  identifiedValues: PropTypes.array,
  checkFillAnimation: PropTypes.func,
  onChange: PropTypes.func.isRequired,
}

export default ExpensePaymentConditions
